import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import LeftSidebar from './LeftSidebar';
import BottomSidebar from './BottomSidebar';
import { UserSelectedContext } from '../contexts/UserSelectedContext';

/**
 * DashboardLayout Component
 * 
 * The main layout wrapper for all dashboard pages. Provides the consistent
 * sidebar navigation structure.
 * 
 * Props:
 * - children: React components to render in the main content area
 * 
 * Usage:
 * <DashboardLayout>
 *   <YourPageContent />
 * </DashboardLayout>
 * 
 * Features:
 * - Left sidebar with navigation
 * - Responsive layout
 * - Automatic active state management
 */
interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* User Selected Context */
  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) {
    throw new Error('useUserSelected must be used within a UserSelectedProvider');
  }
  const { isBottomBarHidden } = userSelectedContext;

  return (
    <>
      {/* Desktop Layout - Hidden on mobile */}
      {!isMobile ? (
        <div className="flex min-h-screen bg-[var(--main-background-color)]">
          {/* Left Sidebar */}
          <LeftSidebar currentPath={location.pathname} />

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col">
            {/* Page Content */}
            <div className="flex-1 bg-[var(--modal-background)] overflow-auto m-4 ml-1 rounded-3xl shadow-lg">
              {children}
            </div>
          </div>
        </div>
      ) : (
        <div className="min-h-screen flex flex-col w-full max-w-full overflow-x-hidden">
          {/* Main Content Area */}
          <div className="bg-[var(--modal-background)] overflow-auto overflow-x-hidden flex flex-1 w-full max-w-full">
            {children}
          </div>
          {/* Bottom Sidebar - Only visible on mobile */}
          {!isBottomBarHidden && <BottomSidebar currentPath={location.pathname} />}
        </div>
      )}
    </>
  );
};

export default DashboardLayout; 